import { useMemo, useState } from 'react'
import Axios from 'axios'
import useSWR from 'swr'
import {
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ApexOptions } from 'apexcharts'

import { ApexChart } from 'ApexChart'
import { strings } from 'strings'
import { AutoStatsRover, useAutoStatsRovers } from 'api'

import {
  BucketGranularity,
  authHeader,
  fetchJson,
  formatDayLabel,
  formatHourLabel,
  newAndTotalTooltip,
  singleDayBarOptions,
  toPersianDigits,
  useAnalyticsRange,
} from './analyticsShared'
import { UNKNOWN_PROVINCE, useProvinces } from './provinces'
import { BucketDetailsDialog } from './BucketDetailsDialog'

const ALL_PROVINCES = '__all__'

const TOTAL_COLOR = '#7b1fa2'
const RAYMAND_COLOR = '#2e7d32'
const NON_RAYMAND_COLOR = '#c62828'

interface GrowthRoverPoint {
  date: string
  newCount: number
  cumulativeCount: number
  newIds?: number[]
}

interface GrowthResponse {
  rovers?: GrowthRoverPoint[]
  raymandRovers?: GrowthRoverPoint[]
  nonRaymandRovers?: GrowthRoverPoint[]
}

interface ActivityPoint {
  date: string
  rovers?: number
  raymandRovers?: number
  nonRaymandRovers?: number
  roverIds?: number[]
  raymandRoverIds?: number[]
  nonRaymandRoverIds?: number[]
}

interface ActivityResponse {
  points?: ActivityPoint[]
}

async function fetchActivity(
  endpoint: string,
  from: string,
  to: string,
): Promise<ActivityResponse> {
  return fetchJson<ActivityResponse>(endpoint, from, to)
}

async function fetchGrowth(
  endpoint: string,
  from: string,
  to: string,
  granularity: BucketGranularity,
): Promise<GrowthResponse> {
  return fetchJson<GrowthResponse>(endpoint, from, to, granularity)
}

async function fetchGrowthRoversForBucket(
  bucket: string,
  granularity: BucketGranularity,
  raymand: boolean,
): Promise<AutoStatsRover[]> {
  const res = await Axios.get('/auto-stats/rovers', {
    params: {
      bucket,
      granularity,
      mode: 'growth',
      raymand: raymand ? '1' : '0',
    },
    headers: authHeader(),
  })
  return (res.data ?? []) as AutoStatsRover[]
}

interface DailyNewRoverIds {
  raymandIds: number[]
  nonRaymandIds: number[]
}

// Backend's /auto-stats/growth response sometimes omits per-bucket `newIds`
// arrays. Without those IDs we cannot tell which province each new rover
// belongs to and the province-filtered cumulative line collapses to a flat
// baseline. Fetch per-bucket rovers directly so the chart works regardless.
function useDailyNewRoverIds(
  bucketKeys: string[],
  granularity: BucketGranularity,
): {
  byDay: Map<string, DailyNewRoverIds>
  loading: boolean
  error: unknown
} {
  const swrKey =
    bucketKeys.length > 0
      ? ['daily-growth-rover-ids', granularity, bucketKeys.join('|')]
      : null
  const { data, error } = useSWR(swrKey, async () => {
    const calls = bucketKeys.flatMap((k) => [
      fetchGrowthRoversForBucket(k, granularity, true),
      fetchGrowthRoversForBucket(k, granularity, false),
    ])
    const results = await Promise.all(calls)
    const byDay = new Map<string, DailyNewRoverIds>()
    for (let i = 0; i < bucketKeys.length; i++) {
      byDay.set(bucketKeys[i], {
        raymandIds: results[i * 2].map((r) => r.userId),
        nonRaymandIds: results[i * 2 + 1].map((r) => r.userId),
      })
    }
    return byDay
  })
  return {
    byDay: data ?? new Map(),
    // When swrKey is null (no province filter, or no buckets with new rovers)
    // we don't fetch — surface that as "not loading" so the chart renders.
    loading: swrKey !== null && !data && !error,
    error,
  }
}

type SeriesKey = 'total' | 'raymand' | 'nonRaymand'

interface DayData {
  dayKey: string
  raymandIds: number[]
  nonRaymandIds: number[]
  // Activity-specific: total active rover IDs for the day, when the API
  // provides them. Growth flow doesn't need this — it derives totals from
  // the union of raymand/non-raymand new IDs.
  totalIds?: number[]
}

interface SelectedMarker {
  dayKey: string
  series: SeriesKey
}

interface RoverProvinceMap {
  // key: `${raymand ? 'r' : 'n'}:${userId}` → province
  map: Map<string, string>
  raymandUsers: AutoStatsRover[]
  nonRaymandUsers: AutoStatsRover[]
}

function useRoverProvinceMap(
  mode: 'growth' | 'activity',
  from: string,
  to: string,
): {
  data: RoverProvinceMap
  loading: boolean
  error: unknown
  ready: boolean
} {
  const raymandResp = useAutoStatsRovers(from, to, { mode, raymand: true })
  const nonRaymandResp = useAutoStatsRovers(from, to, { mode, raymand: false })
  const { ready: provincesReady, resolve } = useProvinces()

  const data = useMemo<RoverProvinceMap>(() => {
    const map = new Map<string, string>()
    if (!provincesReady) {
      return { map, raymandUsers: [], nonRaymandUsers: [] }
    }
    const addAll = (list: AutoStatsRover[] | undefined, isRaymand: boolean) => {
      if (!list) return
      const prefix = isRaymand ? 'r' : 'n'
      for (const r of list) {
        const lat = r.latitude ?? 0
        const lng = r.longitude ?? 0
        const p =
          lat === 0 && lng === 0
            ? UNKNOWN_PROVINCE
            : (resolve(lat, lng) ?? UNKNOWN_PROVINCE)
        map.set(`${prefix}:${r.userId}`, p)
      }
    }
    addAll(raymandResp.rovers, true)
    addAll(nonRaymandResp.rovers, false)
    return {
      map,
      raymandUsers: raymandResp.rovers ?? [],
      nonRaymandUsers: nonRaymandResp.rovers ?? [],
    }
  }, [provincesReady, raymandResp.rovers, nonRaymandResp.rovers, resolve])

  return {
    data,
    loading: raymandResp.loading || nonRaymandResp.loading || !provincesReady,
    error: raymandResp.error ?? nonRaymandResp.error,
    ready: provincesReady,
  }
}

function provinceOf(
  map: Map<string, string>,
  userId: number,
  isRaymand: boolean,
): string {
  return map.get(`${isRaymand ? 'r' : 'n'}:${userId}`) ?? UNKNOWN_PROVINCE
}

function filterAllowlist(
  ids: number[],
  isRaymand: boolean,
  map: Map<string, string>,
  province: string,
): number[] {
  if (province === ALL_PROVINCES) return ids
  return ids.filter((id) => provinceOf(map, id, isRaymand) === province)
}

function collectProvincesFromRoverMap(
  roverMap: RoverProvinceMap,
  sortedNames: string[],
): string[] {
  const present = new Set<string>()
  for (const r of roverMap.raymandUsers) {
    present.add(provinceOf(roverMap.map, r.userId, true))
  }
  for (const r of roverMap.nonRaymandUsers) {
    present.add(provinceOf(roverMap.map, r.userId, false))
  }
  const out = sortedNames.filter((n) => present.has(n))
  if (present.has(UNKNOWN_PROVINCE)) out.push(UNKNOWN_PROVINCE)
  return out
}

interface CommonRenderProps {
  title: string
  province: string
  setProvince: (p: string) => void
  provincesInData: string[]
  loading: boolean
  error: unknown
  isEmpty: boolean
  emptyMessage: string
  options: ApexOptions | null
  dialogs: JSX.Element
  // Optional extra controls rendered alongside the province dropdown — used
  // for "Show all rovers / raymand / non-raymand" buttons so they sit in the
  // header instead of below the chart, matching the other province cards.
  extraControls?: JSX.Element
}

function CardFrame(props: CommonRenderProps) {
  const classes = useStyles()
  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <Typography className={classes.title}>{props.title}</Typography>
        <div className={classes.controls}>
          <TextField
            select
            size="small"
            variant="outlined"
            value={props.province}
            onChange={(e) => props.setProvince(e.target.value as string)}
            className={classes.provinceSelect}
            SelectProps={{
              MenuProps: { PaperProps: { style: { maxHeight: 320 } } },
            }}
          >
            <MenuItem value={ALL_PROVINCES}>{strings.allProvinces}</MenuItem>
            {props.provincesInData.map((p) => (
              <MenuItem key={p} value={p}>
                {p === UNKNOWN_PROVINCE ? strings.unknownProvince : p}
              </MenuItem>
            ))}
          </TextField>
          {props.extraControls}
        </div>
      </div>
      <div className={classes.chartWrap}>
        <>
          {props.error && (
            <Typography className={classes.status}>
              {strings.error}:{' '}
              {String((props.error as any)?.message ?? props.error)}
            </Typography>
          )}
          {!props.error && props.loading && (
            <div className={classes.status}>
              <CircularProgress size={24} />
              <Typography>{strings.loading}</Typography>
            </div>
          )}
          {!props.error && !props.loading && props.isEmpty && (
            <Typography className={classes.status}>
              {props.emptyMessage}
            </Typography>
          )}
          {!props.error && props.options && (
            <ApexChart options={props.options} className={classes.chart} />
          )}
        </>
      </div>
      {props.dialogs}
    </div>
  )
}

// ---------- Growth (cumulative line) ----------

export function ProvinceRoversGrowthChart() {
  return <ProvinceRoversGrowthCard />
}

function ProvinceRoversGrowthCard() {
  const { bucketSpec, allTimeToIso } = useAnalyticsRange()

  const [province, setProvince] = useState<string>(ALL_PROVINCES)
  const [selected, setSelected] = useState<SelectedMarker | null>(null)
  const [listOpen, setListOpen] = useState<SeriesKey | null>(null)

  const { data: growth, error: growthError } = useSWR<GrowthResponse>(
    [
      '/auto-stats/growth',
      bucketSpec.from,
      bucketSpec.to,
      bucketSpec.granularity,
    ],
    fetchGrowth,
  )

  // Fetch all-time rover→province map so per-province cumulative can start at
  // the proper baseline (rovers first-seen before the chart window).
  const {
    data: roverMap,
    loading: mapLoading,
    error: mapError,
  } = useRoverProvinceMap('growth', EPOCH_ISO, allTimeToIso)

  const { provinceNames } = useProvinces()

  // Per-day rover IDs are only needed when the user filters to a specific
  // province (so the cumulative line splits correctly). In ALL_PROVINCES mode
  // we drive the cumulative line off the API count fields and skip the
  // per-day fetches entirely — this is what made "show all data" fan out into
  // thousands of /auto-stats/rovers calls (one per day × 2). We further trim
  // to days that actually had new rovers in the growth response.
  const dayKeysWithNewRovers = useMemo(() => {
    if (province === ALL_PROVINCES || !growth) return []
    const rIndex = new Map<string, number>()
    const nIndex = new Map<string, number>()
    for (const p of growth.raymandRovers ?? []) rIndex.set(p.date, p.newCount)
    for (const p of growth.nonRaymandRovers ?? [])
      nIndex.set(p.date, p.newCount)
    return bucketSpec.keys.filter(
      (k) => (rIndex.get(k) ?? 0) > 0 || (nIndex.get(k) ?? 0) > 0,
    )
  }, [province, bucketSpec.keys, growth])

  const dailyNew = useDailyNewRoverIds(
    dayKeysWithNewRovers,
    bucketSpec.granularity,
  )

  const error = growthError || mapError || dailyNew.error
  const loading = !growth || mapLoading || dailyNew.loading

  const dayBuckets: DayData[] = useMemo(() => {
    return bucketSpec.keys.map((k) => {
      const day = dailyNew.byDay.get(k)
      return {
        dayKey: k,
        raymandIds: day?.raymandIds ?? [],
        nonRaymandIds: day?.nonRaymandIds ?? [],
      }
    })
  }, [dailyNew.byDay, bucketSpec.keys])

  const provincesInData = useMemo(() => {
    if (roverMap.map.size === 0) return [] as string[]
    return collectProvincesFromRoverMap(roverMap, provinceNames)
  }, [roverMap, provinceNames])

  const filteredPerDay = useMemo(() => {
    return dayBuckets.map((d) => ({
      dayKey: d.dayKey,
      raymandIds: filterAllowlist(d.raymandIds, true, roverMap.map, province),
      nonRaymandIds: filterAllowlist(
        d.nonRaymandIds,
        false,
        roverMap.map,
        province,
      ),
    }))
  }, [dayBuckets, roverMap.map, province])

  const cumulativeSeries = useMemo(() => {
    // Per-day "new" counts. For ALL_PROVINCES we read from API count fields
    // (the backend may omit per-day ID arrays in this case); for province-
    // filtered views we read from the already-filtered ID arrays.
    let raymandNew: number[]
    let nonRaymandNew: number[]
    let totalNew: number[]
    if (province === ALL_PROVINCES) {
      const rIndex = new Map<string, GrowthRoverPoint>()
      const nIndex = new Map<string, GrowthRoverPoint>()
      const tIndex = new Map<string, GrowthRoverPoint>()
      for (const p of growth?.raymandRovers ?? []) rIndex.set(p.date, p)
      for (const p of growth?.nonRaymandRovers ?? []) nIndex.set(p.date, p)
      for (const p of growth?.rovers ?? []) tIndex.set(p.date, p)
      raymandNew = bucketSpec.keys.map((k) => rIndex.get(k)?.newCount ?? 0)
      nonRaymandNew = bucketSpec.keys.map((k) => nIndex.get(k)?.newCount ?? 0)
      totalNew = bucketSpec.keys.map((k) => tIndex.get(k)?.newCount ?? 0)
    } else {
      raymandNew = filteredPerDay.map((d) => d.raymandIds.length)
      nonRaymandNew = filteredPerDay.map((d) => d.nonRaymandIds.length)
      // A user can be both raymand and non-raymand on the same day; dedupe by
      // userId so the total line counts unique rovers.
      totalNew = filteredPerDay.map((d) => {
        const set = new Set<number>()
        for (const id of d.raymandIds) set.add(id)
        for (const id of d.nonRaymandIds) set.add(id)
        return set.size
      })
    }

    // Total all-time count in the current scope (province or all).
    let totalRaymand = 0
    let totalNonRaymand = 0
    let totalAll = 0
    if (province === ALL_PROVINCES) {
      totalRaymand = roverMap.raymandUsers.length
      totalNonRaymand = roverMap.nonRaymandUsers.length
      const totalSet = new Set<number>()
      for (const r of roverMap.raymandUsers) totalSet.add(r.userId)
      for (const r of roverMap.nonRaymandUsers) totalSet.add(r.userId)
      totalAll = totalSet.size
    } else {
      const totalSet = new Set<number>()
      for (const r of roverMap.raymandUsers) {
        if (provinceOf(roverMap.map, r.userId, true) === province) {
          totalRaymand++
          totalSet.add(r.userId)
        }
      }
      for (const r of roverMap.nonRaymandUsers) {
        if (provinceOf(roverMap.map, r.userId, false) === province) {
          totalNonRaymand++
          totalSet.add(r.userId)
        }
      }
      totalAll = totalSet.size
    }

    // Baseline = entities first-seen before the window. cumulative[i] starts
    // at the baseline so the line represents "total to date" — same as the
    // backend's new cumulativeCount semantics.
    const sumR = raymandNew.reduce((a, b) => a + b, 0)
    const sumN = nonRaymandNew.reduce((a, b) => a + b, 0)
    const sumT = totalNew.reduce((a, b) => a + b, 0)
    const baselineR = Math.max(0, totalRaymand - sumR)
    const baselineN = Math.max(0, totalNonRaymand - sumN)
    const baselineT = Math.max(0, totalAll - sumT)

    const raymand: number[] = []
    const nonRaymand: number[] = []
    const total: number[] = []
    let runR = baselineR
    let runN = baselineN
    let runT = baselineT
    for (let i = 0; i < raymandNew.length; i++) {
      runR += raymandNew[i]
      runN += nonRaymandNew[i]
      runT += totalNew[i]
      raymand.push(runR)
      nonRaymand.push(runN)
      total.push(runT)
    }
    return { total, raymand, nonRaymand, totalNew, raymandNew, nonRaymandNew }
  }, [province, growth, bucketSpec.keys, filteredPerDay, roverMap])

  const isEmpty =
    !loading &&
    cumulativeSeries.raymand.every((v) => v === 0) &&
    cumulativeSeries.nonRaymand.every((v) => v === 0) &&
    cumulativeSeries.total.every((v) => v === 0)

  const categories = useMemo(
    () =>
      bucketSpec.keys.map((k) =>
        bucketSpec.granularity === 'hour'
          ? formatHourLabel(k)
          : formatDayLabel(k),
      ),
    [bucketSpec.keys, bucketSpec.granularity],
  )

  const options: ApexOptions | null = useMemo(() => {
    if (loading || isEmpty) return null
    return buildGrowthOptions({
      categories,
      dayKeys: filteredPerDay.map((d) => d.dayKey),
      cumulative: cumulativeSeries,
      onMarkerClick: (dayKey, series) => setSelected({ dayKey, series }),
    })
  }, [loading, isEmpty, categories, filteredPerDay, cumulativeSeries])

  const bucketAllowlist = useMemo(() => {
    if (!selected) return null
    if (province === ALL_PROVINCES) return null
    const day = filteredPerDay.find((d) => d.dayKey === selected.dayKey)
    if (!day) return []
    if (selected.series === 'raymand') return day.raymandIds
    if (selected.series === 'nonRaymand') return day.nonRaymandIds
    // total: union of both, deduped
    const set = new Set<number>()
    for (const id of day.raymandIds) set.add(id)
    for (const id of day.nonRaymandIds) set.add(id)
    return Array.from(set)
  }, [selected, filteredPerDay, province])

  const listAllowlist = useMemo(() => {
    if (listOpen === null) return null
    if (province === ALL_PROVINCES) return null
    if (listOpen === 'total') {
      const set = new Set<number>()
      for (const r of roverMap.raymandUsers) {
        if (provinceOf(roverMap.map, r.userId, true) === province) {
          set.add(r.userId)
        }
      }
      for (const r of roverMap.nonRaymandUsers) {
        if (provinceOf(roverMap.map, r.userId, false) === province) {
          set.add(r.userId)
        }
      }
      return Array.from(set)
    }
    const source =
      listOpen === 'raymand' ? roverMap.raymandUsers : roverMap.nonRaymandUsers
    return source
      .filter(
        (r) =>
          provinceOf(roverMap.map, r.userId, listOpen === 'raymand') ===
          province,
      )
      .map((r) => r.userId)
  }, [listOpen, province, roverMap])

  const provinceLabelFor = (name: string) =>
    name === UNKNOWN_PROVINCE ? strings.unknownProvince : name
  const provinceLabel =
    province === ALL_PROVINCES
      ? strings.allProvinces
      : provinceLabelFor(province)

  const seriesLabel = (s: SeriesKey) =>
    s === 'raymand'
      ? strings.raymandRovers
      : s === 'nonRaymand'
        ? strings.nonRaymandRovers
        : strings.rovers

  const seriesRaymandFlag = (s: SeriesKey | null): boolean | undefined =>
    s === 'raymand' ? true : s === 'nonRaymand' ? false : undefined

  const listAllLabel = (s: SeriesKey) =>
    s === 'raymand'
      ? strings.allRaymandRovers
      : s === 'nonRaymand'
        ? strings.allNonRaymandRovers
        : strings.allRovers

  const csvSlug = (s: SeriesKey) =>
    s === 'raymand' ? 'raymand' : s === 'nonRaymand' ? 'non-raymand' : 'all'

  const dialogs = (
    <>
      <BucketDetailsDialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={
          selected
            ? `${seriesLabel(selected.series)} — ${
                bucketSpec.granularity === 'hour'
                  ? `${formatDayLabel(selected.dayKey.slice(0, 10))} ${formatHourLabel(selected.dayKey)}`
                  : formatDayLabel(selected.dayKey)
              } — ${provinceLabel}`
            : strings.growthOfRovers
        }
        bucket={selected?.dayKey ?? null}
        granularity={bucketSpec.granularity}
        kind="rovers"
        mode="growth"
        raymand={selected ? seriesRaymandFlag(selected.series) : undefined}
        roverUserIdAllowlist={bucketAllowlist}
      />
      <BucketDetailsDialog
        open={listOpen !== null}
        onClose={() => setListOpen(null)}
        title={
          listOpen === null
            ? ''
            : `${listAllLabel(listOpen)} — ${provinceLabel}`
        }
        kind="rovers"
        raymand={seriesRaymandFlag(listOpen)}
        from={EPOCH_ISO}
        to={allTimeToIso}
        csvFilename={`rovers-growth-${listOpen ? csvSlug(listOpen) : 'all'}-${province === ALL_PROVINCES ? 'all' : province}`}
        roverUserIdAllowlist={listAllowlist}
      />
    </>
  )

  return (
    <CardFrame
      title={strings.growthOfRovers}
      province={province}
      setProvince={setProvince}
      provincesInData={provincesInData}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={strings.noGrowthInRange}
      options={options}
      dialogs={dialogs}
      extraControls={
        <ListButtonsControls
          onTotal={() => setListOpen('total')}
          onRaymand={() => setListOpen('raymand')}
          onNonRaymand={() => setListOpen('nonRaymand')}
        />
      }
    />
  )
}

// ---------- Activity (per-day active line) ----------

export function ProvinceRoversActivityChart() {
  return <ProvinceRoversActivityCard />
}

function ProvinceRoversActivityCard() {
  const { bucketSpec } = useAnalyticsRange()

  const [province, setProvince] = useState<string>(ALL_PROVINCES)
  const [selected, setSelected] = useState<SelectedMarker | null>(null)
  const [listOpen, setListOpen] = useState<SeriesKey | null>(null)

  const { data: activity, error: activityError } = useSWR<ActivityResponse>(
    ['/auto-stats/activity', bucketSpec.from, bucketSpec.to],
    fetchActivity,
  )

  const {
    data: roverMap,
    loading: mapLoading,
    error: mapError,
  } = useRoverProvinceMap('activity', bucketSpec.from, bucketSpec.to)

  const { provinceNames } = useProvinces()

  const error = activityError || mapError
  const loading = !activity || mapLoading

  const dayBuckets: DayData[] = useMemo(() => {
    const index = new Map<string, ActivityPoint>()
    for (const p of activity?.points ?? []) index.set(p.date, p)
    return bucketSpec.keys.map((k) => {
      const p = index.get(k)
      return {
        dayKey: k,
        raymandIds: p?.raymandRoverIds ?? [],
        nonRaymandIds: p?.nonRaymandRoverIds ?? [],
        totalIds: p?.roverIds,
      }
    })
  }, [activity, bucketSpec])

  const provincesInData = useMemo(() => {
    if (roverMap.map.size === 0) return [] as string[]
    return collectProvincesFromRoverMap(roverMap, provinceNames)
  }, [roverMap, provinceNames])

  // Resolve a userId's province without knowing the agent flavour — checks
  // raymand first, falls back to non-raymand, defaults to UNKNOWN. Used by
  // the "total" series which doesn't carry an agent flag.
  const provinceOfAny = (userId: number): string => {
    const r = roverMap.map.get(`r:${userId}`)
    if (r) return r
    const n = roverMap.map.get(`n:${userId}`)
    if (n) return n
    return UNKNOWN_PROVINCE
  }

  const filteredPerDay = useMemo(() => {
    return dayBuckets.map((d) => {
      const filteredR = filterAllowlist(
        d.raymandIds,
        true,
        roverMap.map,
        province,
      )
      const filteredN = filterAllowlist(
        d.nonRaymandIds,
        false,
        roverMap.map,
        province,
      )
      let filteredTotal: number[]
      if (d.totalIds && d.totalIds.length > 0) {
        filteredTotal =
          province === ALL_PROVINCES
            ? d.totalIds
            : d.totalIds.filter((id) => provinceOfAny(id) === province)
      } else {
        // API didn't break out a unified rover list — derive total from the
        // union of raymand + non-raymand, deduped by userId.
        const set = new Set<number>()
        for (const id of filteredR) set.add(id)
        for (const id of filteredN) set.add(id)
        filteredTotal = Array.from(set)
      }
      return {
        dayKey: d.dayKey,
        raymandIds: filteredR,
        nonRaymandIds: filteredN,
        totalIds: filteredTotal,
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayBuckets, roverMap.map, province])

  const perDayCounts = useMemo(() => {
    if (province === ALL_PROVINCES) {
      // Drive from API count fields so the chart still renders when the
      // backend omits per-day ID arrays.
      const index = new Map<string, ActivityPoint>()
      for (const p of activity?.points ?? []) index.set(p.date, p)
      return {
        total: bucketSpec.keys.map((k) => {
          const p = index.get(k)
          if (typeof p?.rovers === 'number') return p.rovers
          // API didn't surface a unified count — fall back to the filteredPerDay
          // derived total (union deduped).
          const f = filteredPerDay.find((d) => d.dayKey === k)
          return f?.totalIds.length ?? 0
        }),
        raymand: bucketSpec.keys.map((k) => index.get(k)?.raymandRovers ?? 0),
        nonRaymand: bucketSpec.keys.map(
          (k) => index.get(k)?.nonRaymandRovers ?? 0,
        ),
      }
    }
    return {
      total: filteredPerDay.map((d) => d.totalIds.length),
      raymand: filteredPerDay.map((d) => d.raymandIds.length),
      nonRaymand: filteredPerDay.map((d) => d.nonRaymandIds.length),
    }
  }, [province, activity, bucketSpec, filteredPerDay])

  const isEmpty =
    !loading &&
    perDayCounts.total.every((v) => v === 0) &&
    perDayCounts.raymand.every((v) => v === 0) &&
    perDayCounts.nonRaymand.every((v) => v === 0)

  const categories = useMemo(
    () => bucketSpec.keys.map(formatDayLabel),
    [bucketSpec],
  )

  const options: ApexOptions | null = useMemo(() => {
    if (loading || isEmpty) return null
    if (categories.length === 1) {
      const dayKey = filteredPerDay[0]?.dayKey ?? bucketSpec.keys[0]
      const seriesByIndex: SeriesKey[] = ['total', 'raymand', 'nonRaymand']
      return singleDayBarOptions({
        categoryLabel: categories[0],
        yAxisTitle: strings.activeCount,
        // Total ≈ raymand + nonRaymand union; stacking would double-count.
        stacked: false,
        series: [
          {
            name: strings.activeRovers,
            color: TOTAL_COLOR,
            value: perDayCounts.total[0] ?? 0,
            clickable: (perDayCounts.total[0] ?? 0) > 0,
          },
          {
            name: strings.activeRaymandRovers,
            color: RAYMAND_COLOR,
            value: perDayCounts.raymand[0] ?? 0,
            clickable: (perDayCounts.raymand[0] ?? 0) > 0,
          },
          {
            name: strings.activeNonRaymandRovers,
            color: NON_RAYMAND_COLOR,
            value: perDayCounts.nonRaymand[0] ?? 0,
            clickable: (perDayCounts.nonRaymand[0] ?? 0) > 0,
          },
        ],
        onBarClick: (si) => {
          const series = seriesByIndex[si]
          if (!series) return
          setSelected({ dayKey, series })
        },
      })
    }
    return buildActivityOptions({
      categories,
      dayKeys: filteredPerDay.map((d) => d.dayKey),
      perDay: perDayCounts,
      onMarkerClick: (dayKey, series) => setSelected({ dayKey, series }),
    })
  }, [
    loading,
    isEmpty,
    categories,
    filteredPerDay,
    perDayCounts,
    bucketSpec.keys,
  ])

  const bucketAllowlist = useMemo(() => {
    if (!selected) return null
    if (province === ALL_PROVINCES) return null
    const day = filteredPerDay.find((d) => d.dayKey === selected.dayKey)
    if (!day) return []
    if (selected.series === 'raymand') return day.raymandIds
    if (selected.series === 'nonRaymand') return day.nonRaymandIds
    return day.totalIds
  }, [selected, filteredPerDay, province])

  const listAllowlist = useMemo(() => {
    if (listOpen === null) return null
    if (province === ALL_PROVINCES) return null
    if (listOpen === 'total') {
      const set = new Set<number>()
      for (const r of roverMap.raymandUsers) {
        if (provinceOf(roverMap.map, r.userId, true) === province) {
          set.add(r.userId)
        }
      }
      for (const r of roverMap.nonRaymandUsers) {
        if (provinceOf(roverMap.map, r.userId, false) === province) {
          set.add(r.userId)
        }
      }
      return Array.from(set)
    }
    const source =
      listOpen === 'raymand' ? roverMap.raymandUsers : roverMap.nonRaymandUsers
    return source
      .filter(
        (r) =>
          provinceOf(roverMap.map, r.userId, listOpen === 'raymand') ===
          province,
      )
      .map((r) => r.userId)
  }, [listOpen, province, roverMap])

  const provinceLabelFor = (name: string) =>
    name === UNKNOWN_PROVINCE ? strings.unknownProvince : name
  const provinceLabel =
    province === ALL_PROVINCES
      ? strings.allProvinces
      : provinceLabelFor(province)

  const seriesLabel = (s: SeriesKey) =>
    s === 'raymand'
      ? strings.activeRaymandRovers
      : s === 'nonRaymand'
        ? strings.activeNonRaymandRovers
        : strings.activeRovers

  const seriesRaymandFlag = (s: SeriesKey | null): boolean | undefined =>
    s === 'raymand' ? true : s === 'nonRaymand' ? false : undefined

  const listAllLabel = (s: SeriesKey) =>
    s === 'raymand'
      ? strings.allRaymandRovers
      : s === 'nonRaymand'
        ? strings.allNonRaymandRovers
        : strings.allRovers

  const csvSlug = (s: SeriesKey) =>
    s === 'raymand' ? 'raymand' : s === 'nonRaymand' ? 'non-raymand' : 'all'

  const dialogs = (
    <>
      <BucketDetailsDialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={
          selected
            ? `${seriesLabel(selected.series)} — ${formatDayLabel(selected.dayKey)} — ${provinceLabel}`
            : strings.activeRoversByProvince
        }
        bucket={selected?.dayKey ?? null}
        granularity="day"
        kind="rovers"
        mode="activity"
        raymand={selected ? seriesRaymandFlag(selected.series) : undefined}
        roverUserIdAllowlist={bucketAllowlist}
      />
      <BucketDetailsDialog
        open={listOpen !== null}
        onClose={() => setListOpen(null)}
        title={
          listOpen === null
            ? ''
            : `${listAllLabel(listOpen)} — ${provinceLabel}`
        }
        kind="rovers"
        mode="activity"
        raymand={seriesRaymandFlag(listOpen)}
        from={bucketSpec.from}
        to={bucketSpec.to}
        csvFilename={`rovers-activity-${listOpen ? csvSlug(listOpen) : 'all'}-${province === ALL_PROVINCES ? 'all' : province}`}
        roverUserIdAllowlist={listAllowlist}
      />
    </>
  )

  return (
    <CardFrame
      title={strings.activeRoversByProvince}
      province={province}
      setProvince={setProvince}
      provincesInData={provincesInData}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={strings.noActivityInRange}
      options={options}
      dialogs={dialogs}
      extraControls={
        <ListButtonsControls
          onTotal={() => setListOpen('total')}
          onRaymand={() => setListOpen('raymand')}
          onNonRaymand={() => setListOpen('nonRaymand')}
        />
      }
    />
  )
}

function ListButtonsControls({
  onTotal,
  onRaymand,
  onNonRaymand,
}: {
  onTotal: () => void
  onRaymand: () => void
  onNonRaymand: () => void
}) {
  return (
    <>
      <Button size="small" variant="outlined" onClick={onTotal}>
        {strings.showAllRovers}
      </Button>
      <Button size="small" variant="outlined" onClick={onRaymand}>
        {strings.showAllRaymandRovers}
      </Button>
      <Button size="small" variant="outlined" onClick={onNonRaymand}>
        {strings.showAllNonRaymandRovers}
      </Button>
    </>
  )
}

// ---------- Bar chart overview (cumulative count per province, all time) ----------

const EPOCH_ISO = '2000-01-01T00:00:00Z'

interface SelectedBar {
  province: string
  raymand: boolean
}

interface ProvinceRow {
  name: string
  raymand: number
  nonRaymand: number
}

export function ProvinceRoversGrowthBarChart() {
  const classes = useStyles()
  const { allTimeToIso } = useAnalyticsRange()
  const title = strings.numberOfRoversByProvince
  const emptyMessage = strings.noGrowthInRange
  const csvPrefix = 'rovers-growth'

  const [selected, setSelected] = useState<SelectedBar | null>(null)
  const [listOpen, setListOpen] = useState<boolean | null>(null)

  const {
    data: roverMap,
    loading,
    error,
  } = useRoverProvinceMap('growth', EPOCH_ISO, allTimeToIso)

  const provinceRows: ProvinceRow[] = useMemo(() => {
    const counts = new Map<string, { r: number; n: number }>()
    const add = (list: AutoStatsRover[], isRaymand: boolean) => {
      for (const r of list) {
        const p = provinceOf(roverMap.map, r.userId, isRaymand)
        const cur = counts.get(p) ?? { r: 0, n: 0 }
        if (isRaymand) cur.r += 1
        else cur.n += 1
        counts.set(p, cur)
      }
    }
    add(roverMap.raymandUsers, true)
    add(roverMap.nonRaymandUsers, false)
    const rows: ProvinceRow[] = []
    counts.forEach((v, k) =>
      rows.push({ name: k, raymand: v.r, nonRaymand: v.n }),
    )
    rows.sort((a, b) => b.raymand + b.nonRaymand - (a.raymand + a.nonRaymand))
    return rows
  }, [roverMap])

  const isEmpty =
    !loading && provinceRows.every((r) => r.raymand + r.nonRaymand === 0)

  const provinceLabelFor = (name: string) =>
    name === UNKNOWN_PROVINCE ? strings.unknownProvince : name

  const options: ApexOptions | null = useMemo(() => {
    if (loading || isEmpty || provinceRows.length === 0) return null
    return {
      series: [
        {
          name: strings.raymandRovers,
          data: provinceRows.map((r) => r.raymand),
        },
        {
          name: strings.nonRaymandRovers,
          data: provinceRows.map((r) => r.nonRaymand),
        },
      ],
      chart: {
        type: 'bar',
        fontFamily: 'vazir',
        height: 420,
        width: '100%',
        animations: { enabled: false },
        toolbar: { show: true },
        events: {
          dataPointSelection: (
            _e,
            _ctx,
            cfg: { seriesIndex?: number; dataPointIndex?: number },
          ) => {
            const si = cfg?.seriesIndex ?? 0
            const pi = cfg?.dataPointIndex ?? -1
            const row = provinceRows[pi]
            if (!row) return
            const isRaymand = si === 0
            const value = isRaymand ? row.raymand : row.nonRaymand
            if (value <= 0) return
            setSelected({ province: row.name, raymand: isRaymand })
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '70%',
          dataLabels: { position: 'top' },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) =>
          (val as number) > 0 ? String(Math.round(val as number)) : '',
        offsetY: -8,
        style: { fontSize: '11px', colors: ['#333'] },
      },
      xaxis: {
        categories: provinceRows.map((r) => provinceLabelFor(r.name)),
        labels: { rotate: -45, style: { fontSize: '11px' } },
      },
      yaxis: {
        title: { text: strings.cumulativeCount },
        labels: { formatter: (val) => String(Math.round(Number(val))) },
      },
      legend: { position: 'top', horizontalAlign: 'left' },
      colors: [RAYMAND_COLOR, NON_RAYMAND_COLOR],
      tooltip: {
        y: { formatter: (val) => String(Math.round(val as number)) },
      },
    }
  }, [loading, isEmpty, provinceRows])

  const idsForProvince = (isRaymand: boolean, p: string): number[] => {
    const list = isRaymand ? roverMap.raymandUsers : roverMap.nonRaymandUsers
    return list
      .filter((r) => provinceOf(roverMap.map, r.userId, isRaymand) === p)
      .map((r) => r.userId)
  }

  const selectedAllowlist = useMemo(() => {
    if (!selected) return null
    return idsForProvince(selected.raymand, selected.province)
  }, [selected, roverMap])

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <Typography className={classes.title}>{title}</Typography>
        <div className={classes.controls}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setListOpen(true)}
          >
            {strings.showAllRaymandRovers}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setListOpen(false)}
          >
            {strings.showAllNonRaymandRovers}
          </Button>
        </div>
      </div>
      <div className={classes.barChartWrap}>
        <>
          {error && (
            <Typography className={classes.status}>
              {strings.error}: {String((error as any)?.message ?? error)}
            </Typography>
          )}
          {!error && loading && (
            <div className={classes.status}>
              <CircularProgress size={24} />
              <Typography>{strings.loading}</Typography>
            </div>
          )}
          {!error && !loading && isEmpty && (
            <Typography className={classes.status}>{emptyMessage}</Typography>
          )}
          {!error && options && (
            <ApexChart options={options} className={classes.chart} />
          )}
        </>
      </div>
      <BucketDetailsDialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={
          selected
            ? `${selected.raymand ? strings.raymandRovers : strings.nonRaymandRovers} — ${provinceLabelFor(selected.province)}`
            : title
        }
        kind="rovers"
        mode="growth"
        raymand={selected?.raymand}
        from={EPOCH_ISO}
        to={allTimeToIso}
        csvFilename={
          selected
            ? `${csvPrefix}-${selected.raymand ? 'raymand' : 'non-raymand'}-${selected.province}`
            : csvPrefix
        }
        roverUserIdAllowlist={selectedAllowlist}
      />
      <BucketDetailsDialog
        open={listOpen !== null}
        onClose={() => setListOpen(null)}
        title={
          listOpen === null
            ? ''
            : listOpen
              ? strings.allRaymandRovers
              : strings.allNonRaymandRovers
        }
        kind="rovers"
        mode="growth"
        raymand={listOpen ?? undefined}
        from={EPOCH_ISO}
        to={allTimeToIso}
        csvFilename={`${csvPrefix}-${listOpen ? 'raymand' : 'non-raymand'}`}
      />
    </div>
  )
}

// ---------- Chart option builders ----------

interface GrowthOptionsInput {
  categories: string[]
  dayKeys: string[]
  cumulative: {
    total: number[]
    raymand: number[]
    nonRaymand: number[]
    totalNew: number[]
    raymandNew: number[]
    nonRaymandNew: number[]
  }
  onMarkerClick: (dayKey: string, series: SeriesKey) => void
}

function buildGrowthOptions(input: GrowthOptionsInput): ApexOptions {
  const { categories, dayKeys, cumulative, onMarkerClick } = input
  // Series order matches the click handler's seriesIndex → SeriesKey mapping.
  const seriesByIndex: SeriesKey[] = ['total', 'raymand', 'nonRaymand']
  const newByIndex: number[][] = [
    cumulative.totalNew,
    cumulative.raymandNew,
    cumulative.nonRaymandNew,
  ]
  const colors = [TOTAL_COLOR, RAYMAND_COLOR, NON_RAYMAND_COLOR]
  const discreteMarkers: Array<{
    seriesIndex: number
    dataPointIndex: number
    fillColor: string
    strokeColor: string
    size: number
  }> = []
  newByIndex.forEach((arr, si) => {
    arr.forEach((n, i) => {
      if (n > 0) {
        discreteMarkers.push({
          seriesIndex: si,
          dataPointIndex: i,
          fillColor: colors[si],
          strokeColor: '#fff',
          size: 5,
        })
      }
    })
  })

  return {
    series: [
      { name: strings.rovers, data: cumulative.total },
      { name: strings.raymandRovers, data: cumulative.raymand },
      { name: strings.nonRaymandRovers, data: cumulative.nonRaymand },
    ],
    chart: {
      type: 'line',
      fontFamily: 'vazir',
      height: 360,
      animations: { enabled: false },
      toolbar: { show: true },
      events: {
        markerClick: (
          _e,
          _ctx,
          cfg: { seriesIndex?: number; dataPointIndex?: number },
        ) => {
          const si = cfg?.seriesIndex ?? 0
          const pi = cfg?.dataPointIndex ?? -1
          if (pi < 0 || !dayKeys[pi]) return
          const newCount = newByIndex[si]?.[pi] ?? 0
          if (newCount <= 0) return
          const series = seriesByIndex[si]
          if (!series) return
          onMarkerClick(dayKeys[pi], series)
        },
      },
    },
    stroke: { curve: 'straight', width: 3 },
    markers: { size: 0, discrete: discreteMarkers, hover: { size: 6 } },
    dataLabels: {
      // Label every series at points where the cumulative line steps up
      // (newCount > 0). Suppressing zero-delta points keeps labels from
      // repeating across long flat stretches.
      enabled: true,
      formatter: (_val, opts) => {
        const o = opts as { dataPointIndex?: number; seriesIndex?: number }
        const si = o.seriesIndex ?? 0
        const pi = o.dataPointIndex ?? -1
        const newCount = newByIndex[si]?.[pi] ?? 0
        if (newCount <= 0) return ''
        const totals = [
          cumulative.total,
          cumulative.raymand,
          cumulative.nonRaymand,
        ]
        const cum = totals[si]?.[pi] ?? 0
        return toPersianDigits(cum)
      },
      offsetY: -8,
      style: { fontSize: '11px', colors },
      background: { enabled: false },
    },
    xaxis: {
      categories,
      labels: { rotate: -45, style: { fontSize: '11px' } },
    },
    yaxis: {
      min: 0,
      title: { text: strings.cumulativeCount },
      labels: { formatter: (val) => String(Math.round(val as number)) },
    },
    legend: { position: 'top', horizontalAlign: 'left' },
    colors,
    tooltip: newAndTotalTooltip([
      {
        name: strings.rovers,
        color: TOTAL_COLOR,
        total: cumulative.total,
        newCount: cumulative.totalNew,
      },
      {
        name: strings.raymandRovers,
        color: RAYMAND_COLOR,
        total: cumulative.raymand,
        newCount: cumulative.raymandNew,
      },
      {
        name: strings.nonRaymandRovers,
        color: NON_RAYMAND_COLOR,
        total: cumulative.nonRaymand,
        newCount: cumulative.nonRaymandNew,
      },
    ]),
  }
}

interface ActivityOptionsInput {
  categories: string[]
  dayKeys: string[]
  perDay: { total: number[]; raymand: number[]; nonRaymand: number[] }
  onMarkerClick: (dayKey: string, series: SeriesKey) => void
}

function buildActivityOptions(input: ActivityOptionsInput): ApexOptions {
  const { categories, dayKeys, perDay, onMarkerClick } = input
  const seriesByIndex: SeriesKey[] = ['total', 'raymand', 'nonRaymand']
  const valuesByIndex: number[][] = [
    perDay.total,
    perDay.raymand,
    perDay.nonRaymand,
  ]
  const colors = [TOTAL_COLOR, RAYMAND_COLOR, NON_RAYMAND_COLOR]
  const discreteMarkers: Array<{
    seriesIndex: number
    dataPointIndex: number
    fillColor: string
    strokeColor: string
    size: number
  }> = []
  valuesByIndex.forEach((arr, si) => {
    arr.forEach((n, i) => {
      if (n > 0) {
        discreteMarkers.push({
          seriesIndex: si,
          dataPointIndex: i,
          fillColor: colors[si],
          strokeColor: '#fff',
          size: 5,
        })
      }
    })
  })

  return {
    series: [
      { name: strings.activeRovers, data: perDay.total },
      { name: strings.activeRaymandRovers, data: perDay.raymand },
      { name: strings.activeNonRaymandRovers, data: perDay.nonRaymand },
    ],
    chart: {
      type: 'line',
      fontFamily: 'vazir',
      height: 360,
      animations: { enabled: false },
      toolbar: { show: true },
      events: {
        markerClick: (
          _e,
          _ctx,
          cfg: { seriesIndex?: number; dataPointIndex?: number },
        ) => {
          const si = cfg?.seriesIndex ?? 0
          const pi = cfg?.dataPointIndex ?? -1
          if (pi < 0 || !dayKeys[pi]) return
          const val = valuesByIndex[si]?.[pi] ?? 0
          if (val <= 0) return
          const series = seriesByIndex[si]
          if (!series) return
          onMarkerClick(dayKeys[pi], series)
        },
      },
    },
    stroke: { curve: 'straight', width: 3 },
    markers: { size: 0, discrete: discreteMarkers, hover: { size: 6 } },
    dataLabels: { enabled: false },
    xaxis: {
      categories,
      labels: { rotate: -45, style: { fontSize: '11px' } },
    },
    yaxis: {
      min: 0,
      title: { text: strings.activeCount },
      labels: { formatter: (val) => String(Math.round(val as number)) },
    },
    legend: { position: 'top', horizontalAlign: 'left' },
    colors,
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (val) => String(Math.round(val as number)) },
    },
  }
}

const useStyles = makeStyles((theme) => ({
  card: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
    width: '100%',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    background: 'silver',
    flexWrap: 'wrap',
  },
  title: {
    fontWeight: 600,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  provinceSelect: {
    minWidth: 160,
    background: '#fff',
    '& .MuiOutlinedInput-inputMarginDense': {
      paddingTop: 6,
      paddingBottom: 6,
    },
  },
  chartWrap: {
    width: '100%',
    height: 360,
  },
  barChartWrap: {
    width: '100%',
    height: 460,
  },
  chart: {
    width: '100%',
    height: '100%',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    height: 360,
    justifyContent: 'center',
  },
}))
